/**
 * Runs `worker(item)` for every item with at most `concurrency` in flight, and never rejects:
 * one failure is recorded and the rest carry on. Returns one result per item, in input order:
 * `{ item, status: "done" | "skipped" | "failed" | "cancelled", value?, error? }`.
 *
 * A worker returns `runBatch.SKIPPED` when there was nothing to do for that item.
 * `shouldStop()` is checked before each item starts; items already running still finish, so a
 * stop never leaves a request half-sent.
 */
const SKIPPED = Symbol("skipped");

export const runBatch = async (
  items,
  worker,
  { concurrency = 3, onProgress, shouldStop } = {},
) => {
  const list = Array.from(items || []);
  const results = new Array(list.length);
  let next = 0;
  let finished = 0;

  const report = () => {
    if (onProgress) onProgress({ finished, total: list.length, results });
  };

  const lane = async () => {
    while (next < list.length) {
      const index = next;
      next += 1;
      const item = list[index];
      if (shouldStop && shouldStop()) {
        results[index] = { item, status: "cancelled" };
      } else {
        try {
          const value = await worker(item, index);
          results[index] =
            value === SKIPPED ? { item, status: "skipped" } : { item, status: "done", value };
        } catch (error) {
          results[index] = {
            item,
            status: "failed",
            error: error?.message || String(error || "Failed"),
          };
        }
      }
      finished += 1;
      report();
    }
  };

  const lanes = Math.max(1, Math.min(concurrency, list.length));
  await Promise.all(Array.from({ length: lanes }, lane));
  return results;
};

runBatch.SKIPPED = SKIPPED;

export const summarizeBatch = (results) =>
  (results || []).reduce(
    (acc, r) => {
      if (r) acc[r.status] += 1;
      return acc;
    },
    { done: 0, skipped: 0, failed: 0, cancelled: 0 },
  );
